import React from 'react';
import { useSelector } from 'react-redux';

import { logger, LoggerScope } from '~/main/utils/logger';
import { ANALYTICS_EVENTS } from '~/renderer/store/middleware/types';
import { getIsSaleVisible } from '~/renderer/store/receipt/selectors';
import { isBrowserCFD } from '~/renderer/utils';
import { track } from '~/renderer/utils/analytics';
import Logger from '~/renderer/utils/logger';

import type { AppState } from '~/renderer/store/types';

interface GetVersionProps {
  defaultVersion?: string;
  pollVersionDelay?: number;
  reloadDelay?: number;
}

let pollVersionTimeout: number | undefined;
let reloadTimeout: number | undefined;
const POLL_VERSION_TIMEOUT_DELAY = 300000; // 5m
const RELOAD_TIMEOUT_DELAY = 15000; // 15s

const useGetVersion = ({
  defaultVersion = '',
  pollVersionDelay = POLL_VERSION_TIMEOUT_DELAY,
  reloadDelay = RELOAD_TIMEOUT_DELAY,
}: GetVersionProps = {}) => {
  const [currentVersion, setCurrentVersion] = React.useState<string>(defaultVersion);
  const [isDifferentVersion, setIsDifferentVersion] = React.useState<boolean>(false);
  const isSaleVisible = useSelector<AppState, boolean>(getIsSaleVisible);
  const currentVersionRef = React.useRef<string>(currentVersion);

  React.useEffect(() => {
    currentVersionRef.current = currentVersion;
  });

  const pollVersion = () => {
    pollVersionTimeout = window.setTimeout(async () => {
      const version = await getVersion();

      if (!version || currentVersionRef.current === version) {
        pollVersion();
      }
    }, pollVersionDelay);
  };

  const getVersion = async () => {
    let version = '';

    try {
      const response = await fetch(window.location.href);
      const body = await response.text();

      const [, bodyVersion] = body.match(/renderer.cfd.bundle.js\?(\w+)/) || [];

      if (bodyVersion) {
        version = bodyVersion;
      }

      if (!currentVersionRef.current) {
        setCurrentVersion(version);
        Logger({ scope: LoggerScope.CFD }).debug({
          msg: `The current version of the CFD is ${version}`,
        });
      } else if (version && currentVersionRef.current !== version) {
        setIsDifferentVersion(true);

        await track(ANALYTICS_EVENTS.NEW_BROWSER_CFD_VERSION);
      }
    } catch (err) {
      Logger({ scope: LoggerScope.CFD }).error({ err });
    }
    return version;
  };

  React.useEffect(() => {
    // Only available on browser CFD
    if (!isBrowserCFD) return;

    getVersion().catch(err => {
      logger({ scope: LoggerScope.APP }).warn({ err });
    });
    pollVersion();

    return () => window.clearTimeout(pollVersionTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isSaleVisible && reloadTimeout) {
      clearTimeout(reloadTimeout);
      reloadTimeout = undefined;
      Logger({ scope: LoggerScope.CFD }).debug({ msg: 'Page reload canceled' });
    }
  }, [isSaleVisible]);

  React.useEffect(() => {
    if (isDifferentVersion && !isSaleVisible) {
      reloadTimeout = window.setTimeout(() => {
        window.location.reload();
      }, reloadDelay);
      Logger({ scope: LoggerScope.CFD }).debug({
        msg: `Page will reload in ${reloadDelay / 1000} seconds`,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDifferentVersion, isSaleVisible]);

  return {
    version: currentVersion,
    reload: isDifferentVersion,
  };
};

export default useGetVersion;
